import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  addDoc,
  query,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { sendLeadEmail } from "../../services/messagingApi";
import { useSwipeable } from "react-swipeable";
import {
  TrashIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/solid";
import { getAuth } from "firebase/auth";
import { sendNotification } from "../../utils/sendNotification";
import {
  ACTIVE_STAGE_IDS,
  LEAD_STAGES,
  formatFollowUpDate,
  getFollowUpUrgency,
  getLeadStage,
  getLeadStageId,
  getNextStage,
  getProductLine,
} from "../../utils/leadJourney";
import {
  PRODUCT_LINE_QUESTION,
  getQuestionsForLine,
  getScoreBadge,
} from "../../utils/leadQualification";
import PageHeader from "../../components/ui/PageHeader";
import DataToolbar from "../../components/ui/DataToolbar";
import FilterChip from "../../components/ui/FilterChip";
import EmptyState from "../../components/ui/EmptyState";
import { ADMIN_PAGE_WIDTH, cx } from "../../components/ui/utils";

const API_BASE = "https://us-central1-tarjeta-ad945.cloudfunctions.net";

const conversationIdForLead = (lead = {}) => {
  if (lead.messengerPsid) return `fb_${String(lead.messengerPsid).replace(/\D/g, "")}`;
  if (lead.phone) return `wa_${String(lead.phone).replace(/\D/g, "")}`;
  return `email_${lead.id}`;
};

async function updateLead(leadId, data) {
  const res = await fetch(`${API_BASE}/updateLeadStatus`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ leadId, ...data }),
  });
  if (!res.ok) throw new Error(await res.text());
}

// Plantillas de campaña ramificadas por línea de producto (ver leadJourney.js
// PRODUCT_LINES). Sin línea conocida se manda un pitch general que menciona
// ambas — nunca se asume BookMe/reservas por defecto.
const CONTACT_PHONE = "787-236-9189";
const WHATSAPP_NUMBER = "15558364840";

const buildCampaignTemplates = (lead = {}) => {
  const source = (lead.source || "").toLowerCase();
  const templates = [];
  const firstName = lead.name?.split(" ")[0] || "Hola";
  const productLine = getProductLine(lead);

  if (productLine) {
    templates.push({
      id: `${productLine.id}-demo`,
      label: `Propuesta ${productLine.label}`,
      channel: "email",
      subject: `¡Tu demo de ${productLine.label} está lista!`,
      message: `${firstName}, te escribe BBTS sobre ${productLine.shortPitch}. Tu demo personalizada está lista: ${productLine.demoUrl} • Tel ${CONTACT_PHONE}. ¿Confirmamos tu espacio?`,
      html: `<p>Hola ${firstName},</p><p>Te escribe <strong>BBTS</strong> — ${productLine.shortPitch}.</p><p><a href="${productLine.demoUrl}" target="_blank">Confirma aquí tu demo personalizada</a>.</p><p>¿Prefieres que te llamemos? Escríbenos a <a href="mailto:info@bbtspr.com">info@bbtspr.com</a> o al <strong>${CONTACT_PHONE}</strong>.</p><p>Equipo BBTS</p>`,
    });
  }

  if ((lead.aiScore || 0) >= 80) {
    const productHint = productLine ? ` de ${productLine.label}` : "";
    templates.push({
      id: "hot-lead-reminder",
      label: "Follow-up inmediato",
      channel: "sms",
      message: `${firstName}, te habla BBTS. Tenemos apartada tu propuesta${productHint}. Contáctanos al ${CONTACT_PHONE} o responde este mensaje para agendar hoy.`,
    });
  }

  if (source.includes("tiktok") || source.includes("instagram")) {
    templates.push({
      id: "social-proof",
      label: "Campaña redes sociales",
      channel: "email",
      subject: "Te guardamos un espacio exclusivo",
      message: `${firstName}, somos BBTS. Vimos tu interés y apartamos una demo para ti. Ejemplos reales: https://bbtspr.com/casos. Escríbenos a info@bbtspr.com o ${CONTACT_PHONE} para reservar.`,
      html: `<p>Hola ${firstName},</p><p>Te saluda el equipo de <strong>BBTS</strong>. Tenemos un espacio exclusivo para mostrarte cómo trabajamos.</p><p>Mira ejemplos reales aquí: <a href="https://bbtspr.com/casos" target="_blank">Casos BBTS</a>.</p><p>Respóndenos a <a href="mailto:info@bbtspr.com">info@bbtspr.com</a> o llama al <strong>${CONTACT_PHONE}</strong> para asegurar tu asesoría.</p>`,
    });
  }

  if (templates.length === 0) {
    // Sin línea de producto ni fuente reconocida: pitch general de ambas líneas.
    templates.push({
      id: "general-reminder",
      label: "Recordatorio general",
      channel: "whatsapp",
      message: `${firstName}, te habla BBTS. Construimos CRM/ERP a la medida para empresas y sistemas para restaurantes (POS/Clover, menú, reservas). Escríbenos a info@bbtspr.com o WhatsApp ${WHATSAPP_NUMBER} para contarte más.`,
    });
    templates.push({
      id: "bbts-advantages-email",
      label: "Ventajas BBTS (email)",
      channel: "email",
      subject: "Cómo BBTS puede ayudar a tu negocio",
      message: `${firstName}, somos BBTS. Construimos software a la medida: CRM/ERP integral para empresas y sistemas para restaurantes. Agenda tu demo en info@bbtspr.com o ${CONTACT_PHONE}.`,
      html: `<p>Hola ${firstName},</p>
        <p>Te escribe el equipo de <strong>BBTS</strong> para contarte qué hacemos:</p>
        <ul>
          <li><strong>CRM/ERP a la medida</strong>: ventas, proyectos, recursos humanos y facturación en un solo sistema.</li>
          <li><strong>Software para restaurantes</strong>: POS/Clover, menú digital, reservas y delivery.</li>
        </ul>
        <p>¿Te gustaría verlo en acción? Escríbenos a <a href="mailto:info@bbtspr.com">info@bbtspr.com</a> o llama/WhatsApp al <strong>${CONTACT_PHONE}</strong>.</p>
        <p>Equipo BBTS</p>`,
    });
  }

  return templates;
};

async function convertLead(leadId) {
  const res = await fetch(`${API_BASE}/convertLeadToClient`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ leadId }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

const sourceBadge = (s = "") => "bg-orange-100 text-orange-800";

const normalizeText = (value = "") =>
  String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// Filtros rápidos de la barra superior. `activos` es el default: lo que sigue en juego.
const URGENCY_FILTERS = [
  { id: "activos", label: "Activos" },
  { id: "vencido", label: "Vencidos" },
  { id: "hoy", label: "Hoy" },
  { id: "frio", label: "Fríos" },
  { id: "sin_programar", label: "Sin programar" },
  { id: "todos", label: "Todos" },
];

const LeadsList = () => {
  const location = useLocation();
  const [leads, setLeads] = useState([]);
  const [notification, setNotification] = useState("");
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [stageFilter, setStageFilter] = useState("todas");
  const [urgencyFilter, setUrgencyFilter] = useState("activos");
  const [searchTerm, setSearchTerm] = useState(() => new URLSearchParams(location.search).get("lead") || "");
  const [qualifyLead, setQualifyLead] = useState(null);
  const [qualifyAnswers, setQualifyAnswers] = useState({});
  const [qualifySaving, setQualifySaving] = useState(false);
  const [qualifyError, setQualifyError] = useState("");
  const [timelineLead, setTimelineLead] = useState(null);
  const [timelineEntries, setTimelineEntries] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [noteForm, setNoteForm] = useState({
    summary: "",
    channel: "whatsapp",
    schedule: "",
    nextStep: "",
  });
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [sendingAiEmail, setSendingAiEmail] = useState(false);
  const [sendingAiSms, setSendingAiSms] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [conversationMessages, setConversationMessages] = useState([]);
  const [conversationLoading, setConversationLoading] = useState(false);
  const [replyChannel, setReplyChannel] = useState("email");
  const [replySubject, setReplySubject] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState("");
  const auth = getAuth();

  const fetchLeads = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "leads"));
      const leadsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setLeads(leadsData);
    } catch (error) {
      console.error("Error al obtener leads:", error);
    }
  };

  // Cuántos leads hay en cada etapa (sobre el total, no sobre lo filtrado).
  const stageCounts = useMemo(() => {
    const counts = {};
    leads.forEach((lead) => {
      const stageId = getLeadStageId(lead);
      counts[stageId] = (counts[stageId] || 0) + 1;
    });
    return counts;
  }, [leads]);

  // Cuántos requieren acción, para los chips de la barra de filtros.
  const urgencyCounts = useMemo(() => {
    const counts = {};
    leads.forEach((lead) => {
      const urgency = getFollowUpUrgency(lead).id;
      counts[urgency] = (counts[urgency] || 0) + 1;
      if (ACTIVE_STAGE_IDS.includes(getLeadStageId(lead))) {
        counts.activos = (counts.activos || 0) + 1;
      }
    });
    counts.todos = leads.length;
    return counts;
  }, [leads]);

  const visibleLeads = useMemo(() => {
    const term = normalizeText(searchTerm).trim();

    const filtered = leads.filter((lead) => {
      const stageId = getLeadStageId(lead);
      if (stageFilter !== "todas" && stageId !== stageFilter) return false;

      if (urgencyFilter === "activos") {
        if (!ACTIVE_STAGE_IDS.includes(stageId)) return false;
      } else if (urgencyFilter !== "todos") {
        if (getFollowUpUrgency(lead).id !== urgencyFilter) return false;
      }

      if (term) {
        const haystack = normalizeText(
          [lead.id, lead.name, lead.email, lead.phone, lead.messengerPsid, lead.source].filter(Boolean).join(" ")
        );
        if (!haystack.includes(term)) return false;
      }

      return true;
    });

    // Lo más urgente primero; dentro de la misma urgencia, lo más viejo arriba.
    return filtered.sort((a, b) => {
      const urgencyDiff = getFollowUpUrgency(a).order - getFollowUpUrgency(b).order;
      if (urgencyDiff !== 0) return urgencyDiff;

      const aDate = a.nextFollowUpAt?.toDate?.() || a.createdAt?.toDate?.() || null;
      const bDate = b.nextFollowUpAt?.toDate?.() || b.createdAt?.toDate?.() || null;
      if (aDate && bDate) return aDate - bDate;
      if (aDate) return -1;
      if (bDate) return 1;
      return 0;
    });
  }, [leads, stageFilter, urgencyFilter, searchTerm]);

  const handleStageChange = async (lead, stageId) => {
    if (!stageId || stageId === getLeadStageId(lead)) return;

    // Optimista: la tabla responde de inmediato y se reconcilia al recargar.
    setLeads((prev) =>
      prev.map((item) => (item.id === lead.id ? { ...item, stage: stageId } : item))
    );

    try {
      await updateLead(lead.id, { stage: stageId });
      setNotification(
        `${lead.name || lead.email || "Lead"} → ${LEAD_STAGES.find((s) => s.id === stageId)?.label}`
      );
      setTimeout(() => setNotification(""), 2500);
      fetchLeads();
    } catch (error) {
      console.error("Error actualizando etapa del lead:", error);
      setNotification("No se pudo actualizar la etapa");
      setTimeout(() => setNotification(""), 3000);
      fetchLeads();
    }
  };

  const openQualifyModal = (lead) => {
    setQualifyLead(lead);
    setQualifyError("");
    // Precarga lo ya contestado para poder corregir sin empezar de cero.
    const existing = lead.qualification || {};
    setQualifyAnswers({
      productLine: existing.productLine || lead.productLine || "",
      employees: existing.employees || "",
      locations: existing.locations || "",
      currentSystem: existing.currentSystem || "",
      currentPos: existing.currentPos || "",
      timeline: existing.timeline || "",
      needs: Array.isArray(existing.needs) ? existing.needs : [],
    });
  };

  const closeQualifyModal = () => {
    setQualifyLead(null);
    setQualifyAnswers({});
    setQualifyError("");
    setQualifySaving(false);
  };

  const setQualifyAnswer = (questionId, value, multi = false) => {
    setQualifyAnswers((prev) => {
      if (!multi) return { ...prev, [questionId]: value };
      const current = Array.isArray(prev[questionId]) ? prev[questionId] : [];
      return {
        ...prev,
        [questionId]: current.includes(value)
          ? current.filter((item) => item !== value)
          : [...current, value],
      };
    });
  };

  const qualifyQuestions = qualifyAnswers.productLine
    ? getQuestionsForLine(qualifyAnswers.productLine)
    : [];

  // Se exige contestar todo lo de una sola opción; `needs` puede ir vacío.
  const qualifyComplete =
    Boolean(qualifyAnswers.productLine) &&
    qualifyQuestions.every((q) => q.multi || Boolean(qualifyAnswers[q.id]));

  const handleQualifySubmit = async () => {
    if (!qualifyLead || !qualifyComplete) return;
    setQualifySaving(true);
    setQualifyError("");

    try {
      const res = await fetch(`${API_BASE}/saveLeadQualification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          leadId: qualifyLead.id,
          answers: qualifyAnswers,
          answeredBy: auth.currentUser?.email || "admin",
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "No se pudo guardar la calificación");
      }

      setNotification(
        `${qualifyLead.name || "Lead"} calificado: score ${data.score} (${data.priority})`
      );
      setTimeout(() => setNotification(""), 4000);
      closeQualifyModal();
      fetchLeads();
    } catch (error) {
      console.error("Error guardando calificación:", error);
      setQualifyError(error.message || "No se pudo guardar la calificación");
      setQualifySaving(false);
    }
  };

  const openTimelineModal = async (lead) => {
    setTimelineLead(lead);
    setAiSuggestion(null);
    setAiError("");
    setTimelineLoading(true);
    setReplyChannel(lead.messengerPsid ? "messenger" : lead.email ? "email" : "whatsapp");
    setReplySubject(`Re: ${lead.name || lead.email || "tu consulta"}`);
    setReplyMessage("");
    setReplyError("");
    try {
      const timelineRef = collection(db, "leads", lead.id, "timeline");
      const timelineSnap = await getDocs(query(timelineRef, orderBy("createdAt", "desc")));
      const entries = timelineSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setTimelineEntries(entries);
    } catch (error) {
      console.error("Error al cargar seguimiento:", error);
    } finally {
      setTimelineLoading(false);
    }

    // Hilo real de comunicación (el mismo que usa Mensajería) — para
    // contestar con contexto, no a ciegas.
    setConversationLoading(true);
    try {
      const convRef = collection(db, "messaging_conversations", conversationIdForLead(lead), "messages");
      const convSnap = await getDocs(query(convRef, orderBy("createdAt", "desc"), limit(5)));
      setConversationMessages(
        convSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })).reverse()
      );
    } catch (error) {
      console.error("Error al cargar la conversación:", error);
      setConversationMessages([]);
    } finally {
      setConversationLoading(false);
    }
  };

  const handleReplySend = async () => {
    if (!timelineLead || !replyMessage.trim()) return;
    setReplySending(true);
    setReplyError("");
    try {
      if (replyChannel === "email") {
        if (!timelineLead.email) throw new Error("Este lead no tiene correo guardado.");
        await sendLeadEmail({
          profileId: timelineLead.id,
          subject: replySubject.trim() || "Mensaje de BBTS",
          message: replyMessage.trim(),
        });
      } else if (replyChannel === "messenger") {
        if (!timelineLead.messengerPsid) throw new Error("Este lead no tiene Messenger conectado.");
        const res = await fetch(`${API_BASE}/sendWhatsAppReply`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ psid: timelineLead.messengerPsid, message: replyMessage.trim() }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || "No se pudo enviar por Messenger");
        }
      } else {
        const phone = timelineLead.phone?.trim();
        if (!phone) throw new Error("Este lead no tiene teléfono guardado.");
        const res = await fetch(`${API_BASE}/sendWhatsAppReply`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ phone, message: replyMessage.trim() }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || "Error al enviar por WhatsApp");
        }
      }
      setReplyMessage("");
      setNotification(`Mensaje enviado a ${timelineLead.name || timelineLead.email}`);
      setTimeout(() => setNotification(""), 3000);
      openTimelineModal(timelineLead);
    } catch (error) {
      console.error("Error al responder:", error);
      setReplyError(error.message || "No se pudo enviar el mensaje.");
    } finally {
      setReplySending(false);
    }
  };

  const closeTimelineModal = () => {
    setTimelineLead(null);
    setTimelineEntries([]);
    setNoteForm({
      summary: "",
      channel: "whatsapp",
      schedule: "",
      nextStep: "",
    });
    setSendingCampaign(false);
    setSendingAiEmail(false);
    setSendingAiSms(false);
    setAiSuggestion(null);
    setAiError("");
    setAiLoading(false);
    setConversationMessages([]);
    setReplyMessage("");
    setReplySubject("");
    setReplyError("");
  };

  const handleGenerateAiSuggestion = async () => {
    if (!timelineLead) return;
    setAiLoading(true);
    setAiError("");
    setAiSuggestion(null);
    try {
      const payload = {
        lead: {
          id: timelineLead.id,
          name: timelineLead.name,
          email: timelineLead.email,
          phone: timelineLead.phone,
          source: timelineLead.source,
          aiScore: timelineLead.aiScore,
          lastContactNote: timelineLead.lastContactNote,
          productLine: timelineLead.productLine,
        },
      };
      const res = await fetch(`${API_BASE}/generateLeadFollowUp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "No se pudo generar sugerencia");
      }
      setAiSuggestion(data.data);
    } catch (error) {
      console.error("Error generando sugerencia AI:", error);
      setAiError(error.message || "No se pudo generar sugerencia AI");
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiSuggestion = () => {
    if (!aiSuggestion) return;
    const contactCTA = " Contáctanos al 787-236-9189, WhatsApp 555-836-4840 o info@bbtspr.com.";
    const summaryText = `${aiSuggestion.suggestedMessage}${contactCTA}`.trim();
    setNoteForm((prev) => ({
      ...prev,
      summary: summaryText,
      channel: aiSuggestion.aiChannel,
    }));
  };

  const sendAiSuggestion = async (channel) => {
    if (!timelineLead || !aiSuggestion) return;
    const contactCTA = " Contáctanos al 787-236-9189, WhatsApp 555-836-4840 o info@bbtspr.com.";
    const message = `${aiSuggestion.suggestedMessage || ""}${contactCTA}`.trim();
    const setLoading = channel === "email" ? setSendingAiEmail : setSendingAiSms;
    setLoading(true);
    try {
      if (channel === "email") {
        if (!timelineLead.email) throw new Error("El lead no tiene correo");
        await sendNotification("email", {
          to: timelineLead.email,
          subject: aiSuggestion.priority === "alta" ? "Seguimiento urgente BookMe" : "Seguimiento BookMe",
          html: `<p>${message}</p><p>- Equipo BookMe</p>`,
          text: message,
        });
      } else {
        const phone = timelineLead.phone?.trim();
        if (!phone) throw new Error("El lead no tiene teléfono");
        const digits = phone.replace(/[^0-9]/g, "");
        const formattedPhone = phone.startsWith("+")
          ? phone
          : digits.length === 10
          ? `+1${digits}`
          : `+${digits}`;
        await sendNotification("sms", {
          to: formattedPhone,
          message,
        });
      }

      await addDoc(collection(db, "leads", timelineLead.id, "timeline"), {
        eventType: "seguimiento_ia",
        summary: `Seguimiento AI (${channel})`,
        channel,
        aiContext: aiSuggestion,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || "admin",
      });
      setNotification(`Seguimiento AI enviado por ${channel} a ${timelineLead.name || timelineLead.email}`);
      setTimeout(() => setNotification(""), 2500);
    } catch (error) {
      console.error("Error enviando sugerencia AI:", error);
      setAiError(error.message || "No se pudo enviar la sugerencia AI");
    } finally {
      setLoading(false);
    }
  };

  const handleTimelineSubmit = async (e) => {
    e.preventDefault();
    if (!timelineLead || !noteForm.summary.trim()) return;

    try {
      const timelineRef = collection(db, "leads", timelineLead.id, "timeline");
      const remindDate = noteForm.schedule ? Timestamp.fromDate(new Date(noteForm.schedule)) : null;
      const payload = {
        eventType: "contacto",
        summary: noteForm.summary.trim(),
        channel: noteForm.channel,
        nextStep: noteForm.nextStep.trim() || null,
        remindAt: remindDate,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || "admin",
      };

      await addDoc(timelineRef, payload);

      await updateDoc(doc(db, "leads", timelineLead.id), {
        lastContactAt: serverTimestamp(),
        lastContactNote: payload.summary,
        nextFollowUpAt: remindDate,
        nextStep: payload.nextStep,
      }).catch(() => {});

      if (noteForm.schedule) {
        try {
          const isoSchedule = new Date(noteForm.schedule).toISOString();
          await fetch(`${API_BASE}/scheduleLeadReminder`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              schedule: isoSchedule,
              summary: payload.summary,
              channel: noteForm.channel,
              lead: {
                id: timelineLead.id,
                name: timelineLead.name,
                email: timelineLead.email,
                phone: timelineLead.phone,
              },
            }),
          });
        } catch (err) {
          console.error("No se pudo agendar recordatorio en Calendar:", err);
        }
      }

      setNoteForm({
        summary: "",
        channel: noteForm.channel,
        schedule: "",
        nextStep: "",
      });
      openTimelineModal(timelineLead);
      setNotification(`Seguimiento guardado para ${timelineLead.name || timelineLead.email}`);
      setTimeout(() => setNotification(""), 3000);
    } catch (error) {
      console.error("Error guardando seguimiento:", error);
    }
  };

  const handleSendCampaign = async (template) => {
    if (!timelineLead) return;
    setSendingCampaign(true);
    try {
      if (template.channel === "email") {
        await sendNotification("email", {
          to: timelineLead.email,
          subject: template.subject || "Seguimiento BBTS",
          html:
            template.html ||
            `<p>${template.message}</p><p>Equipo BBTS</p>`,
        });
      } else if (template.channel === "sms" || template.channel === "whatsapp") {
        const phone = timelineLead.phone?.trim();
        if (!phone) throw new Error("El lead no tiene teléfono");
        const formattedPhone = phone.startsWith("+") ? phone : `+1${phone}`;
        await sendNotification("sms", {
          to: formattedPhone,
          message: template.message,
        });
      }

      const timelineRef = collection(db, "leads", timelineLead.id, "timeline");
      await addDoc(timelineRef, {
        eventType: "campana_enviada",
        summary: `Campaña "${template.label}" enviada`,
        channel: template.channel,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || "admin",
        type: "campaign",
      });
      openTimelineModal(timelineLead);
      setNotification(`Campaña "${template.label}" enviada a ${timelineLead.name || timelineLead.email}`);
      setTimeout(() => setNotification(""), 3000);
    } catch (error) {
      console.error("Error enviando campaña:", error);
      setNotification(`No se pudo enviar la campaña: ${template.label}`);
      setTimeout(() => setNotification(""), 3000);
    } finally {
      setSendingCampaign(false);
    }
  };

  const handleDelete = (lead) => {
    setLeadToDelete(lead);
  };

  const confirmDelete = async () => {
    if (!leadToDelete) return;
    try {
      await deleteDoc(doc(db, "leads", leadToDelete.id));
      setNotification(`Lead "${leadToDelete.name}" eliminado`);
      setLeadToDelete(null);
      fetchLeads();
      setTimeout(() => setNotification(""), 3000);
    } catch (error) {
      console.error("Error eliminando lead:", error);
    }
  };

  const cancelDelete = () => {
    setLeadToDelete(null);
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const campaignTemplates = timelineLead ? buildCampaignTemplates(timelineLead) : [];

  const LeadCard = ({ lead }) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const swipeHandlers = useSwipeable({
      onSwipedLeft: () => {
        setIsDeleting(true);
        setTimeout(() => handleDelete(lead), 300);
      },
      preventScrollOnSwipe: true,
      trackMouse: true,
    });
  
    return (
      <div
        {...swipeHandlers}
        className={`p-4 rounded-lg shadow border transition-all duration-300 ${
          isDeleting ? "bg-red-100" : "bg-white"
        }`}
      >
        <div className="flex justify-between items-start">
          <div>
            <p className="font-semibold text-gray-800">{lead.name || "—"}</p>
            <p className="text-sm text-gray-600">{lead.email}</p>
            <p className="text-sm text-gray-600">{lead.phone}</p>
            <div className="flex gap-2 mt-2 flex-wrap items-center">
              <span className={`px-2 py-1 text-xs rounded ${sourceBadge(lead.source)}`}>
                {lead.source || "—"}
              </span>
              <span
                className={`px-2 py-1 text-xs rounded font-medium ${getLeadStage(lead).badge}`}
              >
                {getLeadStage(lead).label}
              </span>
              <span
                className={`px-2 py-1 text-xs rounded font-medium ${getFollowUpUrgency(lead).badge}`}
              >
                {getFollowUpUrgency(lead).label}
              </span>
              {lead.qualification ? (
                <button
                  onClick={() => openQualifyModal(lead)}
                  className={`px-2 py-1 text-xs rounded font-semibold hover:opacity-75 ${getScoreBadge(
                    lead.aiScore || 0
                  )}`}
                  title="Ver / editar respuestas del cuestionario"
                >
                  Score {lead.aiScore ?? 0}
                </button>
              ) : (
                <button
                  onClick={() => openQualifyModal(lead)}
                  className="px-2 py-1 text-xs rounded font-medium bg-blue-100 text-blue-700 hover:bg-blue-200"
                >
                  Calificar
                </button>
              )}
            </div>

            {lead.nextStep && (
              <p className="mt-2 text-sm text-gray-700">
                <span className="text-gray-400">Próximo paso: </span>
                {lead.nextStep}
                {formatFollowUpDate(lead.nextFollowUpAt) && (
                  <span className="text-gray-400">
                    {" "}
                    ({formatFollowUpDate(lead.nextFollowUpAt)})
                  </span>
                )}
              </p>
            )}

            {/* ✅ Acciones agregadas para móvil */}
            <div className="flex flex-wrap gap-2 mt-4">
              <select
                value={getLeadStageId(lead)}
                onChange={(e) => handleStageChange(lead, e.target.value)}
                className={`rounded border-0 px-2 py-1 text-xs font-medium ${getLeadStage(lead).badge}`}
              >
                {LEAD_STAGES.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>

              {/* Convertir */}
              {getLeadStageId(lead) !== "cliente" && (
                <button
                  onClick={async () => {
                    try {
                      await convertLead(lead.id);
                      await updateLead(lead.id, { stage: "cliente" });
                      setNotification(`Lead ${lead.name} convertido a cliente`);
                      fetchLeads();
                    } catch (e) {
                      console.error(e);
                      setNotification("No se pudo convertir el lead");
                      setTimeout(() => setNotification(""), 3000);
                    }
                  }}
                  className="text-xs px-3 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200"
                >
                  Convertir
                </button>
              )}

              {/* Tus acciones originales */}
              <a
                href={`sms:${lead.phone}?body=Hola ${lead.name}, gracias por tu interés.`}
                className="bg-orange-100 text-orange-700 hover:bg-orange-200 p-2 rounded-full"
                title="Enviar SMS"
              >
                <PaperAirplaneIcon className="h-5 w-5" />
              </a>
              <a
                href={`https://wa.me/${lead.phone}?text=Hola ${lead.name}, gracias por tu interés.`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-100 text-green-700 hover:bg-green-200 p-2 rounded-full"
                title="Enviar WhatsApp"
              >
                <PaperAirplaneIcon className="h-5 w-5" />
              </a>
              <button
                onClick={() => openTimelineModal(lead)}
                className="text-xs px-3 py-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200"
              >
                Seguimiento
              </button>
              <button
                onClick={() => handleDelete(lead)}
                className="bg-red-100 text-red-700 hover:bg-red-200 p-2 rounded-full"
                title="Eliminar Lead"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  

  return (
    <div className="min-h-screen bg-slate-50">
      <div className={cx("mx-auto space-y-5 px-4 py-6 sm:px-6 lg:px-8", ADMIN_PAGE_WIDTH)}>
        <PageHeader
          title="Leads capturados"
          description={
            <>
              {visibleLeads.length} de {leads.length}
              {urgencyCounts.vencido ? (
                <span className="ml-2 font-semibold text-red-600">
                  · {urgencyCounts.vencido} vencido{urgencyCounts.vencido === 1 ? "" : "s"}
                </span>
              ) : null}
              {urgencyCounts.hoy ? (
                <span className="ml-2 font-semibold text-amber-600">
                  · {urgencyCounts.hoy} para hoy
                </span>
              ) : null}
            </>
          }
          actions={
            <>
              <Link
                to="/admin/leads/embudo"
                className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-orange-300 hover:text-orange-600"
              >
                Ver embudo
              </Link>
              <button
                onClick={fetchLeads}
                className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-600"
              >
                <ArrowPathIcon className="h-5 w-5" />
                Recargar
              </button>
            </>
          }
        />

        {/* Notificación */}
        {notification && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-800">
            {notification}
          </div>
        )}

        {/* Filtros */}
        <DataToolbar className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <FilterChip
              label="Todas las etapas"
              count={leads.length}
              active={stageFilter === "todas"}
              onClick={() => setStageFilter("todas")}
            />
            {LEAD_STAGES.map((stage) => (
              <FilterChip
                key={stage.id}
                label={stage.label}
                count={stageCounts[stage.id] || 0}
                active={stageFilter === stage.id}
                onClick={() => setStageFilter(stage.id)}
                title={stage.description}
              >
                <span className={cx("h-2 w-2 rounded-full", stage.dot)} />
              </FilterChip>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
            {URGENCY_FILTERS.map((filter) => (
              <FilterChip
                key={filter.id}
                label={filter.label}
                count={urgencyCounts[filter.id] || 0}
                active={urgencyFilter === filter.id}
                onClick={() => setUrgencyFilter(filter.id)}
              />
            ))}
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar nombre, email, teléfono…"
              className="ml-auto w-full max-w-sm rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none"
            />
          </div>
        </DataToolbar>

        {/* 📱 Vista móvil */}
        <div className="block lg:hidden space-y-4">
          {visibleLeads.length === 0 ? (
            <EmptyState
              label={
                leads.length === 0
                  ? "No hay leads disponibles"
                  : "Ningún lead coincide con los filtros"
              }
            />
          ) : (
            visibleLeads.map((lead) => <LeadCard key={lead.id} lead={lead} />)
          )}
        </div>

      {/* 💻 Vista escritorio */}
      <div className="hidden overflow-x-auto rounded-3xl border border-slate-200 bg-white shadow-sm lg:block">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-5 py-3 text-left">Contacto</th>
              <th className="px-4 py-3 text-left">Fuente</th>
              <th className="px-4 py-3 text-left">Etapa</th>
              <th className="px-4 py-3 text-left">Próximo paso</th>
              <th className="px-4 py-3 text-left">Seguimiento</th>
              <th className="px-5 py-3 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {visibleLeads.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-5 py-6">
                  <EmptyState
                    label={
                      leads.length === 0
                        ? "No hay leads disponibles"
                        : "Ningún lead coincide con los filtros"
                    }
                  />
                </td>
              </tr>
            ) : (
              visibleLeads.map((lead) => {
                const stage = getLeadStage(lead);
                const urgency = getFollowUpUrgency(lead);
                const nextStage = getNextStage(stage.id);
                const productLine = getProductLine(lead);
                const followUpLabel = formatFollowUpDate(lead.nextFollowUpAt);
                const lastContactLabel = formatFollowUpDate(lead.lastContactAt);

                return (
                <tr
                  key={lead.id}
                  className={urgency.id === "vencido" ? "bg-red-50/60" : undefined}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{lead.name || "—"}</div>
                    <div className="text-xs text-gray-500">{lead.email}</div>
                    <div className="text-xs text-gray-500">{lead.phone}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      {productLine && (
                        <span
                          className={`rounded px-1.5 py-0.5 text-[0.65rem] font-medium ${productLine.badge}`}
                        >
                          {productLine.label}
                        </span>
                      )}
                      {lead.qualification ? (
                        <button
                          onClick={() => openQualifyModal(lead)}
                          className={`rounded px-1.5 py-0.5 text-[0.65rem] font-semibold hover:opacity-75 ${getScoreBadge(
                            lead.aiScore || 0
                          )}`}
                          title="Ver / editar respuestas del cuestionario"
                        >
                          Score {lead.aiScore ?? 0}
                        </button>
                      ) : (
                        <button
                          onClick={() => openQualifyModal(lead)}
                          className="rounded bg-blue-100 px-1.5 py-0.5 text-[0.65rem] font-medium text-blue-700 hover:bg-blue-200"
                        >
                          Calificar
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded ${sourceBadge(lead.source)}`}>
                      {lead.source || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={stage.id}
                      onChange={(e) => handleStageChange(lead, e.target.value)}
                      className={`rounded border-0 px-2 py-1 text-xs font-medium focus:ring-2 focus:ring-orange-400 ${stage.badge}`}
                    >
                      {LEAD_STAGES.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {nextStage && (
                      <button
                        onClick={() => handleStageChange(lead, nextStage.id)}
                        className="mt-1 block text-[0.68rem] text-gray-400 hover:text-orange-600"
                        title={nextStage.description}
                      >
                        → {nextStage.label}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 max-w-[16rem]">
                    {lead.nextStep ? (
                      <span className="text-sm text-gray-700">{lead.nextStep}</span>
                    ) : (
                      <span className="text-xs italic text-gray-400">Sin definir</span>
                    )}
                    {lastContactLabel && (
                      <div className="mt-0.5 text-[0.68rem] text-gray-400">
                        Últ. contacto: {lastContactLabel}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block rounded px-2 py-1 text-xs font-medium ${urgency.badge}`}
                    >
                      {urgency.label}
                    </span>
                    {followUpLabel && urgency.id !== "cerrado" && (
                      <div className="mt-0.5 text-[0.68rem] text-gray-500">{followUpLabel}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {(lead.messengerPsid || lead.phone) && (
                        <Link
                          to="/admin/whatsapp"
                          state={{ conversationId: conversationIdForLead(lead) }}
                          className="px-3 py-1 text-xs rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
                        >
                          Conversación
                        </Link>
                      )}
                      {/* Convertir a cliente. La etapa se maneja desde el selector. */}
                      {stage.id !== "cliente" && (
                        <button
                          onClick={async () => {
                            try {
                              await convertLead(lead.id);
                              await updateLead(lead.id, { stage: "cliente" });
                              setNotification(`Lead ${lead.name} convertido a cliente`);
                              fetchLeads();
                            } catch (e) {
                              console.error(e);
                              setNotification("No se pudo convertir el lead");
                              setTimeout(() => setNotification(""), 3000);
                            }
                          }}
                          className="px-3 py-1 text-xs rounded bg-green-100 text-green-700 hover:bg-green-200"
                        >
                          Convertir
                        </button>
                      )}

                      {/* SMS / WhatsApp / Eliminar */}
                      <button
                        onClick={() => openTimelineModal(lead)}
                        className="px-3 py-1 text-xs rounded bg-purple-100 text-purple-700 hover:bg-purple-200"
                      >
                        Seguimiento
                      </button>
                      <a
                        href={`sms:${lead.phone}?body=Hola ${lead.name}, gracias por tu interés.`}
                        className="bg-orange-100 text-orange-700 hover:bg-orange-200 p-2 rounded-full"
                        title="SMS"
                      >
                        <PaperAirplaneIcon className="h-5 w-5" />
                      </a>
                      <a
                        href={`https://wa.me/${lead.phone}?text=Hola ${lead.name}, gracias por tu interés.`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-green-100 text-green-700 hover:bg-green-200 p-2 rounded-full"
                        title="WhatsApp"
                      >
                        <PaperAirplaneIcon className="h-5 w-5" />
                      </a>
                      <button
                        onClick={() => handleDelete(lead)}
                        className="bg-red-100 text-red-700 hover:bg-red-200 p-2 rounded-full"
                        title="Eliminar"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      </div>

      {/* Modal de calificación */}
      {qualifyLead && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-lg">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  {qualifyLead.qualification ? "Respuestas de " : "Calificar a "}
                  {qualifyLead.name || qualifyLead.email || "lead"}
                </h3>
                <p className="text-xs text-gray-500">
                  {qualifyLead.qualification
                    ? `Score ${qualifyLead.aiScore ?? 0} (${qualifyLead.qualification.priority || "—"}) — contestado por ${qualifyLead.qualification.answeredBy || "desconocido"}`
                    : "4 preguntas. El score se calcula solo."}
                </p>
              </div>
              <button
                onClick={closeQualifyModal}
                className="text-gray-500 hover:text-red-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-5 px-6 py-5">
              {qualifyLead.qualification && (
                <p className="rounded-md bg-emerald-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                  Respuestas que dio el contacto — no son editables aquí
                </p>
              )}
              {/* Pregunta que rutea */}
              <div>
                <p className="mb-2 text-sm font-medium text-gray-800">
                  {PRODUCT_LINE_QUESTION.label}
                </p>
                {qualifyLead.qualification ? (
                  <p className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">
                    {PRODUCT_LINE_QUESTION.options.find((o) => o.value === qualifyAnswers.productLine)?.label || qualifyAnswers.productLine}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {PRODUCT_LINE_QUESTION.options.map((option) => (
                      <button
                        key={option.value}
                        onClick={() =>
                          setQualifyAnswer(PRODUCT_LINE_QUESTION.id, option.value)
                        }
                        className={`rounded-md border px-3 py-2 text-sm transition ${
                          qualifyAnswers.productLine === option.value
                            ? "border-orange-500 bg-orange-50 font-medium text-orange-700"
                            : "border-gray-200 text-gray-600 hover:border-orange-300"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Preguntas de la línea seleccionada */}
              {qualifyQuestions.map((question) => (
                <div key={question.id}>
                  <p className="mb-1 text-sm font-medium text-gray-800">
                    {question.label}
                    {question.multi && (
                      <span className="ml-1 text-xs font-normal text-gray-400">
                        (varias)
                      </span>
                    )}
                  </p>
                  {question.hint && (
                    <p className="mb-2 text-xs text-gray-400">{question.hint}</p>
                  )}
                  {qualifyLead.qualification ? (
                    <p className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800">
                      {question.multi
                        ? (question.options.filter((o) => (qualifyAnswers[question.id] || []).includes(o.value)).map((o) => o.label).join(', ') || '—')
                        : (question.options.find((o) => o.value === qualifyAnswers[question.id])?.label || qualifyAnswers[question.id] || '—')}
                    </p>
                  ) : (
                  <div className="flex flex-wrap gap-2">
                    {question.options.map((option) => {
                      const selected = question.multi
                        ? (qualifyAnswers[question.id] || []).includes(option.value)
                        : qualifyAnswers[question.id] === option.value;
                      return (
                        <button
                          key={option.value}
                          onClick={() =>
                            setQualifyAnswer(question.id, option.value, question.multi)
                          }
                          className={`rounded-md border px-3 py-1.5 text-sm transition ${
                            selected
                              ? "border-orange-500 bg-orange-50 font-medium text-orange-700"
                              : "border-gray-200 text-gray-600 hover:border-orange-300"
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                  )}
                </div>
              ))}

              {!qualifyAnswers.productLine && (
                <p className="text-sm text-gray-400">
                  Escoge el tipo de negocio para ver las preguntas.
                </p>
              )}

              {qualifyError && (
                <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-600">
                  {qualifyError}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <button
                onClick={closeQualifyModal}
                className="rounded bg-gray-100 px-4 py-2 text-sm text-gray-700 hover:bg-gray-200"
              >
                {qualifyLead.qualification ? "Cerrar" : "Cancelar"}
              </button>
              {!qualifyLead.qualification && (
                <button
                  onClick={handleQualifySubmit}
                  disabled={!qualifyComplete || qualifySaving}
                  className="rounded bg-orange-500 px-4 py-2 text-sm font-medium text-white hover:bg-orange-600 disabled:opacity-50"
                >
                  {qualifySaving ? "Guardando…" : "Guardar calificación"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación */}
      {leadToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              ¿Eliminar a {leadToDelete.name}?
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de seguimiento */}
      {timelineLead && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center px-4">
          <div className="bg-white w-full max-w-3xl rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Seguimiento de {timelineLead.name || timelineLead.email}
                </h3>
                <p className="text-xs text-gray-500">
                  Estado actual: {timelineLead.status || "nuevo"} • Score:{" "}
                  {timelineLead.aiScore ?? "—"}
                </p>
              </div>
              <button
                onClick={closeTimelineModal}
                className="text-gray-500 hover:text-red-600"
              >
                ✕
              </button>
            </div>

            <div className="border-b px-6 py-4 space-y-3 bg-sky-50/50">
              <p className="text-xs font-semibold text-sky-700 uppercase tracking-wide">
                Responder
              </p>

              {conversationLoading ? (
                <p className="text-xs text-gray-400">Cargando conversación…</p>
              ) : conversationMessages.length > 0 ? (
                <div className="space-y-1.5 max-h-28 overflow-y-auto rounded border border-sky-100 bg-white p-2">
                  {conversationMessages.map((msg) => (
                    <p key={msg.id} className="text-xs text-gray-600">
                      <span className={`font-semibold ${msg.direction === "inbound" ? "text-emerald-600" : "text-gray-400"}`}>
                        {msg.direction === "inbound" ? "Cliente: " : "Tú: "}
                      </span>
                      {msg.textBody?.slice(0, 140) || "(sin texto)"}
                    </p>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">
                  Sin mensajes previos con este contacto todavía.
                </p>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setReplyChannel("email")}
                  className={`px-3 py-1 text-xs rounded-full font-medium ${
                    replyChannel === "email"
                      ? "bg-sky-600 text-white"
                      : "bg-white text-sky-700 border border-sky-200"
                  }`}
                >
                  Por correo
                </button>
                <button
                  type="button"
                  onClick={() => setReplyChannel("whatsapp")}
                  disabled={!timelineLead.phone}
                  className={`px-3 py-1 text-xs rounded-full font-medium disabled:opacity-40 ${
                    replyChannel === "whatsapp"
                      ? "bg-emerald-600 text-white"
                      : "bg-white text-emerald-700 border border-emerald-200"
                  }`}
                  title={!timelineLead.phone ? "Este lead no tiene teléfono guardado" : ""}
                >
                  Por WhatsApp
                </button>
                <button
                  type="button"
                  onClick={() => setReplyChannel("messenger")}
                  disabled={!timelineLead.messengerPsid}
                  className={`px-3 py-1 text-xs rounded-full font-medium disabled:opacity-40 ${
                    replyChannel === "messenger"
                      ? "bg-blue-600 text-white"
                      : "bg-white text-blue-700 border border-blue-200"
                  }`}
                  title={!timelineLead.messengerPsid ? "Este lead no llegó por Messenger" : ""}
                >
                  Por Messenger
                </button>
              </div>

              {replyChannel === "email" && (
                <input
                  type="text"
                  value={replySubject}
                  onChange={(e) => setReplySubject(e.target.value)}
                  placeholder="Asunto"
                  className="w-full rounded border px-3 py-1.5 text-sm"
                />
              )}
              <textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder={
                  replyChannel === "email"
                    ? "Escribe tu respuesta…"
                    : replyChannel === "messenger"
                      ? "Escribe tu mensaje de Messenger…"
                      : "Escribe tu mensaje de WhatsApp…"
                }
                rows={3}
                className="w-full rounded border px-3 py-1.5 text-sm"
              />
              {replyError && <p className="text-xs text-red-600">{replyError}</p>}
              <button
                type="button"
                onClick={handleReplySend}
                disabled={replySending || !replyMessage.trim()}
                className="rounded bg-sky-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
              >
                {replySending ? "Enviando…" : "Enviar"}
              </button>
            </div>

            {campaignTemplates.length > 0 && (
              <div className="px-6 py-3 bg-amber-50 border-b border-amber-100 space-y-2">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                  Campañas sugeridas por IA
                </p>
                <div className="flex flex-wrap gap-2">
                  {campaignTemplates.map((template) => (
                    <button
                      key={template.id}
                      disabled={sendingCampaign}
                      onClick={() => handleSendCampaign(template)}
                      className="px-3 py-1 text-xs rounded-full bg-amber-200 text-amber-800 hover:bg-amber-300 disabled:opacity-50"
                    >
                      {sendingCampaign ? "Enviando..." : template.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-0">
              <div className="border-r px-6 py-4 space-y-4">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Historial de notas
                </h4>
                {timelineLoading ? (
                  <p className="text-sm text-gray-500">Cargando…</p>
                ) : timelineEntries.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    Aún no hay notas registradas.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {timelineEntries.map((entry) => (
                      <li
                        key={entry.id}
                        className="border rounded-lg p-3 text-sm space-y-1"
                      >
                        <p className="text-gray-800">{entry.summary}</p>
                        {entry.nextStep && (
                          <p className="text-xs text-blue-600">
                            Próximo paso: {entry.nextStep}
                          </p>
                        )}
                        {entry.remindAt && (
                          <p className="text-xs text-gray-500">
                            Recordar el{" "}
                            {entry.remindAt.toDate
                              ? entry.remindAt
                                  .toDate()
                                  .toLocaleString("es-PR")
                              : new Date(entry.remindAt).toLocaleString(
                                  "es-PR"
                                )}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 flex justify-between">
                          <span>Canal: {entry.channel}</span>
                          <span>
                            {entry.createdAt?.toDate
                              ? entry.createdAt
                                  .toDate()
                                  .toLocaleString("es-PR")
                              : "—"}
                          </span>
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="px-6 py-4 bg-gray-50">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                  Nueva nota / Automatización
                </h4>
                <div className="space-y-3 mb-4">
                  <button
                    type="button"
                    onClick={handleGenerateAiSuggestion}
                    disabled={aiLoading}
                    className="w-full border border-dashed border-orange-300 text-orange-700 text-sm py-2 rounded hover:bg-orange-50 disabled:opacity-60"
                  >
                    {aiLoading ? 'Generando sugerencia...' : 'Generar sugerencia con IA'}
                  </button>
                  {aiError && (
                    <p className="text-xs text-red-500">{aiError}</p>
                  )}
                  {aiSuggestion && (
                    <div className="border border-orange-200 bg-white rounded-lg p-3 text-sm space-y-2">
                      <div className="flex items-center justify-between text-xs uppercase text-orange-600 font-semibold">
                        <span>Canal: {aiSuggestion.aiChannel}</span>
                        <span>Prioridad: {aiSuggestion.priority}</span>
                        <span>Score: {aiSuggestion.aiScore}</span>
                      </div>
                      <p className="text-gray-700 whitespace-pre-line">
                        {aiSuggestion.suggestedMessage}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={applyAiSuggestion}
                          className="px-3 py-1 text-xs bg-orange-500 text-white rounded hover:bg-orange-600"
                        >
                          Usar en seguimiento
                        </button>
                        <button
                          type="button"
                          onClick={() => sendAiSuggestion("email")}
                          disabled={sendingAiEmail}
                          className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
                        >
                          {sendingAiEmail ? "Enviando..." : "Enviar por email"}
                        </button>
                        <button
                          type="button"
                          onClick={() => sendAiSuggestion("sms")}
                          disabled={sendingAiSms || !timelineLead?.phone}
                          className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60"
                        >
                          {sendingAiSms ? "Enviando..." : "Enviar por SMS"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setAiSuggestion(null)}
                          className="px-3 py-1 text-xs text-gray-500 border border-gray-200 rounded"
                        >
                          Limpiar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <form className="space-y-4" onSubmit={handleTimelineSubmit}>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">
                      Resumen del contacto
                    </label>
                    <textarea
                      className="w-full border rounded p-2 text-sm"
                      rows={3}
                      value={noteForm.summary}
                      onChange={(e) =>
                        setNoteForm({ ...noteForm, summary: e.target.value })
                      }
                      placeholder="Ej: Llamada completada, interesado en BookMe."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">
                      Próximo paso
                    </label>
                    <input
                      className="w-full border rounded p-2 text-sm"
                      value={noteForm.nextStep}
                      onChange={(e) =>
                        setNoteForm({ ...noteForm, nextStep: e.target.value })
                      }
                      placeholder="Ej: enviar demo, agendar llamada, etc."
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600">
                        Canal sugerido
                      </label>
                      <select
                        className="w-full border rounded p-2 text-sm"
                        value={noteForm.channel}
                        onChange={(e) =>
                          setNoteForm({ ...noteForm, channel: e.target.value })
                        }
                      >
                        <option value="whatsapp">WhatsApp</option>
                        <option value="sms">SMS</option>
                        <option value="email">Email</option>
                        <option value="llamada">Llamada</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600">
                        Programar recordatorio
                      </label>
                      <input
                        type="datetime-local"
                        className="w-full border rounded p-2 text-sm"
                        value={noteForm.schedule}
                        onChange={(e) =>
                          setNoteForm({ ...noteForm, schedule: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded text-sm font-medium"
                  >
                    Guardar seguimiento
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsList;
